import { ccfRoutes } from '@app/ccf/ccf-routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateCategoryStatus } from '@shared/api/hooks/ccf/reports';
import {
  CompanyReportCategory,
  CompanyReportCategoryStatusStatusEnum,
} from '@shared/api/types';
import SelectFormField from '@shared/components/form/select-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { object, string, z } from 'zod';
import { useCCF } from '../ccf-context';

const excludeCategoryFormSchema = object({
  reason: string({
    required_error: 'Please select a reason',
  }).min(1),
});

interface ExcludeCategoryDialogProps {
  category: CompanyReportCategory;
  closeDialog: () => void;
}

const reasonOptions = [
  {
    label: 'Not required',
    value: CompanyReportCategoryStatusStatusEnum.not_required,
  },
  {
    label: 'No emissions to report',
    value: CompanyReportCategoryStatusStatusEnum.no_emissions,
  },
  {
    label: 'Data unavailable',
    value: CompanyReportCategoryStatusStatusEnum.data_unavailable,
  },
];

export default function ExcludeCategoryDialog({
  category,
  closeDialog,
}: ExcludeCategoryDialogProps) {
  const navigate = useNavigate();
  const { uuid: reportUuid } = useCCF();
  const { toast } = useToast();
  const { mutateAsync: updateCategoryStatus, isLoading: isSubmitting } =
    useUpdateCategoryStatus();

  const currentStatus = category.status;
  const defaultReason = reasonOptions.some((o) => o.value === currentStatus)
    ? currentStatus
    : undefined;

  const form = useForm<z.infer<typeof excludeCategoryFormSchema>>({
    resolver: zodResolver(excludeCategoryFormSchema),
    defaultValues: {
      reason: defaultReason,
    },
  });
  const { handleSubmit } = form;

  const onSubmit = async (data: z.infer<typeof excludeCategoryFormSchema>) => {
    try {
      await updateCategoryStatus({
        companyReportUuid: reportUuid!,
        data: {
          activityType: category.slug,
          status: data.reason as CompanyReportCategoryStatusStatusEnum,
        },
      });
      toast({
        title: 'Category excluded',
        variant: 'success',
      });
      closeDialog();
      navigate(
        generatePath(ccfRoutes.COMPANY_REPORT, {
          report_uuid: reportUuid!,
        })
      );
    } catch (error) {
      toast({
        title: 'Failed to exclude category',
        variant: 'destructive',
      });
    }
  };

  return (
    <Form {...form}>
      <form
        className="max-w-content-center space-y-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Your published report will show either a reason for exclusion or a
            zero figure for emissions if you select{' '}
            <em>No emissions to report</em>.
          </p>
          <SelectFormField
            name="reason"
            label="Reason for exclusion"
            options={reasonOptions}
            placeholder="Select a reason"
            className="max-w-input"
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            size="lg"
            variant="secondary"
            disabled={isSubmitting}
            onClick={closeDialog}
          >
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={isSubmitting}>
            Exclude category
          </Button>
        </div>
      </form>
    </Form>
  );
}
